import { EventEmitter } from '../utils/event-emitter.js';
import { DataLoader } from '../utils/data-loader.js';
import { EVENTS } from '../utils/constants.js';
import { MovieManager } from './movie-manager.js';
import { CategoryManager } from './category-manager.js';
import { SearchManager } from './search-manager.js';
import { SortManager } from './sort-manager.js';
import { UIManager } from './ui-manager.js';
import { PopupManager } from './popup-manager.js';
import { NavigationManager } from './navigation-manager.js';

/**
 * Основной класс приложения
 * Координирует работу всех модулей
 */
export class App {
  constructor() {
    this.dataLoader = new DataLoader();
    this.movieManager = new MovieManager();
    this.categoryManager = new CategoryManager();
    this.searchManager = new SearchManager();
    this.sortManager = new SortManager();
    this.uiManager = new UIManager();
    this.popupManager = new PopupManager();
    this.navigationManager = new NavigationManager();

    // Состояние приложения
    this.state = {
      movies: [],
      categories: {},
      selectedCategories: [],
      searchQuery: '',
      sortType: 'category',
      sortOrder: 'asc',
      filteredMovies: [],
    };

    // Подписчики на изменения состояния
    this.subscribers = [];
  }

  /**
   * Инициализация приложения
   */
  async init() {
    try {
      console.log('🚀 Инициализация приложения КВАРТ...');

      // Загружаем данные
      await this.loadData();

      // Инициализируем модули
      this.initializeManagers();

      // Настраиваем обработчики событий
      this.setupEventListeners();

      // Первоначальный рендер
      this.updateFilteredMovies();
      this.render();

      console.log('✅ Приложение успешно инициализировано');
    } catch (error) {
      console.error('❌ Ошибка инициализации приложения:', error);
      throw error;
    }
  }

  /**
   * Загрузка данных из JSON файлов
   */
  async loadData() {
    try {
      console.log('📡 Загрузка данных...');

      const [{ movies }, categoriesData] = await Promise.all([
        this.dataLoader.loadMovies(),
        this.dataLoader.loadCategories(),
      ]);

      this.state.categories = categoriesData.categories;

      // создаём карту имя->категория
      const nameToCat = {};
      Object.values(this.state.categories).forEach(cat => {
        nameToCat[cat.name.trim()] = cat;
      });

      // трансформируем фильмы: category -> id и добавляем categoryName
      this.state.movies = movies.map(m => {
        const matchCat = nameToCat[m.category?.trim()] || null;
        return {
          ...m,
          categoryName: m.category,
          category: matchCat ? matchCat.id : m.category, // если не найдено — оставляем как есть
        };
      });

      console.log(`📽️ Загружено ${this.state.movies.length} фильмов`);
      console.log(`📂 Загружено ${Object.keys(this.state.categories).length} категорий`);
    } catch (error) {
      console.error('❌ Ошибка загрузки данных:', error);
      throw new Error('Не удалось загрузить данные приложения');
    }
  }

  /**
   * Инициализация менеджеров
   */
  initializeManagers() {
    console.log('🔧 Инициализация менеджеров...');

    // Передаем данные в менеджеры
    this.movieManager.init(this.state.movies);
    this.categoryManager.init(this.state.categories);
    this.searchManager.init();
    this.sortManager.init();
    // Устанавливаем сортировку по категории по умолчанию
    this.sortManager.setSortSettings('category', 'asc', false);
    this.state.sortType = 'category';
    this.uiManager.init();
    this.popupManager.init();
    this.navigationManager.init();
  }

  /**
   * Настройка обработчиков событий
   */
  setupEventListeners() {
    console.log('🎯 Настройка обработчиков событий...');

    // Поиск
    this.searchManager.on('search', (query) => {
      this.state.searchQuery = query;
      this.updateFilteredMovies();
      this.render();
    });

    // Категории
    this.categoryManager.on(EVENTS.CATEGORIES_CHANGED, (eventData) => {
      console.log('📨 App получил событие CATEGORIES_CHANGED:', eventData);
      this.state.selectedCategories = eventData.selectedCategories;
      this.updateFilteredMovies();
      this.render();
    });

    // Сортировка
    this.sortManager.on('sortChanged', ({ type, order }) => {
      this.state.sortType = type;
      this.state.sortOrder = order;
      this.updateFilteredMovies();
      this.render();
    });

    // UI события
    this.uiManager.on('scrollToTop', () => {
      this.navigationManager.scrollToTop();
    });

    // Popup события
    this.popupManager.on('contactsOpen', () => {
      this.popupManager.openContacts();
    });
    
    // Обработчик закрытия popup - активируем "Каталог"
    this.popupManager.on('setActiveNavigation', (itemText) => {
      this.navigationManager.setActiveNavigationItem(itemText);
    });

    // Обработчик закрытия меню категорий - активируем нужный пункт
    this.categoryManager.on('setActiveNavigation', (itemText) => {
      this.navigationManager.setActiveNavigationItem(itemText);
    });
  }

  /**
   * Обновление отфильтрованного списка фильмов
   */
  updateFilteredMovies() {
    let filteredMovies = [...this.state.movies];

    // Фильтрация по категориям
    if (this.state.selectedCategories.length > 0) {
      filteredMovies = this.movieManager.filterByCategories(
        filteredMovies,
        this.state.selectedCategories,
      );
    }

    // Поиск
    if (this.state.searchQuery) {
      filteredMovies = this.movieManager.search(
        filteredMovies,
        this.state.searchQuery,
      );
    }

    // Сортировка
    filteredMovies = this.movieManager.sort(
      filteredMovies,
      this.state.sortType,
      this.state.sortOrder,
      this.state.categories,
    );

    this.state.filteredMovies = filteredMovies;
  }

  /**
   * Рендеринг приложения
   */
  render() {
    // Рендерим категории
    this.categoryManager.render(this.state.categories, this.state.movies);

    // Рендерим выбранные категории
    this.categoryManager.renderSelectedCategories(
      this.state.selectedCategories,
      this.state.categories,
    );

    // Рендерим фильмы
    this.movieManager.render(this.state.filteredMovies, this.state.categories);

    // Обновляем UI
    this.uiManager.updateMovieCount(this.state.filteredMovies.length);
    this.uiManager.toggleNoResults(this.state.filteredMovies.length === 0);

    // Обновляем бейдж количества фильмов в меню Категории
    const totalBadge = document.getElementById('categoriesTotalCount');
    if (totalBadge) {
      totalBadge.textContent = this.state.movies.length;
    }
  }

  /**
   * Подписка на изменения состояния
   */
  subscribe(callback) {
    this.subscribers.push(callback);
  }

  /**
   * Уведомление подписчиков об изменении состояния
   */
  notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.state));
  }

  /**
   * Получение текущего состояния
   */
  getState() {
    return { ...this.state };
  }
}
